import {
  getClassifiedAs,
  getFieldPartSubfield,
  getValueByClassification,
  normalizeFieldToArray
} from "@thegetty/linkedart.js";
import * as localLinkedArtHelpers from "@/helpers/LinkedArt";
import aat from "@data/aat.json";
import {
  getName,
  getActorDescription,
  getBirthPlace,
  getDeathPlace,
  getBornRange,
  getDiedRange,
  getBirthDateDescription,
  getDeathDateDescription,
  getUlanLink,
  getULANValue,
  getDisplayDate
} from "@thegetty-private/linked-data.js";
import internal from "@/data/internal-ontologies";
import { routerHelpers } from "@thegetty-private/rad-app-utils";

const REFERRED_TO_BY = "referred_to_by";
const SUBJECT_OF = "subject_of";
const RELATED_FROM_BY = "la:related_from_by";
const RELATES_TO = "la:relates_to";
const TOOK_PLACE_AT = "took_place_at";
const EN = "http://vocab.getty.edu/language/en";
const COMMENT_ON_WORKS = "Comment on works:";

/**
 * gets the birth place for a person
 * @param {object} object - the JSON-LD Actor
 *
 * @returns {string}
 */
export function getBornPlace(object) {
  return getBirthPlace(object, internal.BIRTH_PLACE);
}

/**
 * gets the dates for a person
 * @param {object} object - the JSON-LD Actor
 *
 * @returns {string}
 */
export function getDates(object) {
  let dates = getLifeDates(object);
  if (dates == undefined) {
    dates = getTimespan(object);
  }
  return dates;
}

/**
 * gets the description for a person
 * @param {object} object - the JSON-LD Actor
 * @param {format} string - the format (text/html ; text/markdown to request)
 *
 * @returns {string | undefined}
 */
export function getDescription(object, format) {
  if (object == undefined) {
    return;
  }
  return getActorDescription(object, format);
}

/**
 * gets the death place for a person
 * @param {object} object - the JSON-LD Actor
 *
 * @returns {string}
 */
export function getDiedPlace(object) {
  return getDeathPlace(object, internal.DEATH_PLACE);
}

/**
 * gets the textual life dates for a person
 * @param {object} object - the JSON-LD Actor
 *
 * @returns {string}
 */
export function getLifeDates(object) {
  let references = normalizeFieldToArray(object, REFERRED_TO_BY);
  let lifeDateObject = getClassifiedAs(references, internal.LIFE_DATE);
  if (lifeDateObject.length > 0) {
    return lifeDateObject[0].content;
  }
  return undefined;
}

/**
 * Gets the nationality and dates statement for a Person
 *
 * @param {object} object - the JSON-LD Actor
 *
 * @returns {string/undefined}
 *
 */
export function getNationalityAndDates(object) {
  let referredToBy = normalizeFieldToArray(object, REFERRED_TO_BY);

  return getValueByClassification(
    referredToBy,
    internal.ARTIST_NATIONALITY_AND_DATE
  );
}

/**
 * gets the AAT references for a person's Nationality
 * @param {object} object - the JSON-LD Actor
 *
 * @returns {array}
 */
export function getNationalityReferences(object) {
  let nationalities = [];
  for (var obj of getClassifiedAs(object.classified_as, aat.NATIONALITY)) {
    nationalities.push(obj);
  }
  return nationalities;
}

/**
 * gets the page title for the actor page
 * @param {object} object - the JSON-LD Actor
 *
 * @returns {string}
 */
export function getPageTitle(object) {
  return getName(object);
}

/**
 * Takes a Person object and gets the link objects for the related resources section
 * (currently only returning a ULAN link if available)
 *
 * @param {object} object - the JSON-LD of the Person
 * @param {function} translate - the translate function
 *
 * @return {array} an array of link objects to be used for ListOfLinksWithHeading
 * @example [ { iconName: {string}, text: {string}, to: {object} }, { text: {string}, url: string } ]
 *
 */
export function getRelatedResourcesLinks(object, translate) {
  const links = [];

  // construct link for ulan if it exists
  const ulanUrl = getUlanLink(object);
  if (ulanUrl) {
    const ulanLinkObj = {
      iconName: "outbound",
      text: translate("actor_page.ulan_link"),
      url: ulanUrl.replace("vocab.getty.edu/", "vocab.getty.edu/page/")
    };
    links.push(ulanLinkObj);
  }

  return links;
}

/**
 * Takes a Person object and gets the technical metadata fields
 *
 * @param {object} object - the JSON-LD of the Person object
 *
 * @return {object} An object with the Person technical metadata fields to display
 *
 */
export function getTechnicalMetadataFields(object) {
  if (object) {
    return localLinkedArtHelpers.getTechnicalMetadataFields(object);
  }
}

/**
 * This is a function to get a string summarizing a Person object's timespan
 *
 * @param {object} the Person object
 *
 * @return {String} The string summarizing their lifespan: [born year] - [died year]
 */
export function getTimespan(object) {
  let birth = getBirthDateDescription(object, internal.BIRTH_DATE_DESCRIPTION);
  let b_range = getBornRange(object);
  let death = getDeathDateDescription(object, internal.DEATH_DATE_DESCRIPTION);
  let d_range = getDiedRange(object);
  if (!birth && b_range != undefined) {
    birth = getDisplayDate(b_range);
  }
  if (!death && d_range != undefined) {
    death = getDisplayDate(d_range);
  }

  if (!birth && !death) return;
  return birth + " - " + death;
}

/**
 * @param {object} ulanLod - The JSON for a Person from ULAN
 *
 * @return {string | undefined} The first descriptive note from the ulanLod
 */
export function getUlanDescription(ulanLod, lang = EN) {
  const subjectOf = normalizeFieldToArray(ulanLod, SUBJECT_OF);
  let desc = getValueByClassification(subjectOf, aat.DESCRIPTIVE_NOTE, {
    language: lang
  });

  if (desc && desc.includes(COMMENT_ON_WORKS)) {
    desc = desc.substr(0, desc.indexOf(COMMENT_ON_WORKS));
    desc = desc.trim();
  }
  return desc;
}

/**
 * Builds a list of ULAN ids for related actors as defined in a ULAN record
 *
 * @param {object} ulanLod - The JSON for a Person from ULAN
 *
 * @return {array} A list of ULAN ids for related actors
 */
export function getUlanRelatedActors(ulanLod) {
  let related = [];
  const ids = getFieldPartSubfield(ulanLod, RELATED_FROM_BY, RELATES_TO);
  if (ids.length) {
    ids.map((actor) => related.push(actor.id));
  }
  return related;
}

// actor store helpers

/**
 * @param {array} exhibitions - Array of related exhibition results to be transformed
 *
 * @return {array} Array of transformed exhibition data ready for constituentRelatedObjects to consume
 */
export function transformRelatedExhibitionsResults(exhibitions) {
  return exhibitions.map((exhibition) => {
    let slug;
    let heading;
    let subheading;
    // if sitewide search result
    if (exhibition.url) {
      let urlParts = exhibition.url.split("/");
      slug = urlParts[urlParts.length - 1];
      heading = exhibition.title;
      subheading = exhibition.description;
    }

    // if elasticsearch result
    if (exhibition.slug_with_path) {
      slug = routerHelpers.getSlugFromSlugWithPath(exhibition.slug_with_path);
      heading = exhibition.primary_name;
      subheading = exhibition.timespan;
    }

    return {
      id: exhibition.id,
      heading,
      subheading,
      isGridItemClickable: true,
      linkProps: {
        to: {
          name: "exhibition",
          params: {
            slug: slug
          },
          state: {
            indexedId: exhibition.id,
            slug: slug
          }
        }
      }
    };
  });
}

/**
 * @param {array} actors - Array of related actor results to be transformed
 *
 * @return {array} Array of transformed related actor results ready for relatedSearchResults component
 */
export function transformRelatedActorResult(result) {
  if (result == undefined) {
    return {};
  }

  let slug = result.slug_with_path.split("/").pop();
  let heading = result.life_dates
    ? result.primary_name + ", " + result.life_dates
    : result.primary_name;

  let item = {
    heading: heading,
    to: {
      name: "person",
      params: {
        slug: slug
      },
      state: {
        indexedId: result.id,
        slug: slug
      }
    }
  };
  return item;
}

/**
 * @param {object} ulanLod - The JSON for a Person from ULAN
 * @param {object} tgn - an object tgn data with tgn id as key
 *
 * @return {string | undefined} The birth place name from the
 * corresponding tgn object
 */
export function getUlanBirthPlace(ulanLod, tgn) {
  let result = ulanLod.born
    ? normalizeFieldToArray(ulanLod.born, TOOK_PLACE_AT)
    : [];

  // splitting the id to remove "-place" from the url. there's a
  // ticket to drop the it from the id, so we should be able to
  // remove the split after that
  return result.length
    ? getUlanPreferredLocation(result[0].id.split("-")[0], tgn)
    : undefined;
}

/**
 * @param {object} ulanLod - The JSON for a Person from ULAN
 * @param {object} tgn - an object tgn data with tgn id as key
 *
 * @return {string | undefined} The death place name from the
 * corresponding tgn object
 */
export function getUlanDeathPlace(ulanLod, tgn) {
  let result = ulanLod.died
    ? normalizeFieldToArray(ulanLod.died, TOOK_PLACE_AT)
    : [];

  // splitting the id to remove "-place" from the url. there's a
  // ticket to drop the it from the id, so we should be able to
  // remove the split after that
  return result.length
    ? getUlanPreferredLocation(result[0].id.split("-")[0], tgn)
    : undefined;
}

/**
 * @param {object} ulanLod - The JSON for a Person from ULAN. This
 * function is needed because we want to test that the died range is
 * not in the future as that is a requirement of the source system
 *
 * @return {object | undefined} an timespan of the died range from the ulanLod
 * ie. { lte: '1930-12-31 23:59:59', gte: '1930-01-01 00:00:00' }
 */
export function getUlanDiedRange(ulanLod) {
  let diedDate = getDiedRange(ulanLod);

  if (diedDate) {
    // ULAN may have future dates which should get filtered out
    let today = new Date().getTime();
    let compareDate = new Date(diedDate.lte).getTime();

    diedDate = compareDate > today ? undefined : diedDate;
  }

  return diedDate;
}

/**
 * @param {object} ulanLod - The JSON for a Person from ULAN
 * @param {function} translate - the translate function
 *
 * @return {string} a lifespan string ie '1936-2016'
 */
export function getUlanLifeDates(ulanLod, translate) {
  // Parse a ulan datetime into a string, accounting for B.C. dates
  const bc = translate("actor_page.bc");
  function getUlanDateDisplay(ulanDateRaw) {
    let ulanDate = ulanDateRaw.lte.split("-");
    if (ulanDate.length > 3) {
      return `${ulanDate[1].replace(/^0+/, "")} ${bc}`;
    }
    return ulanDate[0].replace(/^0+/, "");
  }

  let lifeDates;
  let birthDateRange = getBornRange(ulanLod);
  let deathDateRange = getUlanDiedRange(ulanLod);

  if (!birthDateRange && !deathDateRange) {
    return;
  }

  let birthDate = birthDateRange ? getUlanDateDisplay(birthDateRange) : "";
  let deathDate = deathDateRange ? getUlanDateDisplay(deathDateRange) : "";

  // assign correct prefix to a single date
  if (birthDate.length && !deathDate.length) {
    lifeDates = `${translate("actor_page.born")} ${birthDate}`;
  }

  if (!birthDate.length && deathDate.length) {
    lifeDates = `{${translate("actor_page.died")} ${deathDate}`;
  }

  // clean up a pair of B.C. dates to only include B.C. once
  if (birthDate.includes(bc) && deathDate.includes(bc)) {
    birthDate = birthDate.replace(bc, "").trim();
  }

  // standard case - birth and death
  if (birthDate.length && deathDate.length) {
    lifeDates = `${birthDate} - ${deathDate}`;
  }

  return lifeDates;
}

/**
 * @param {string} ulanId - a ulan place id
 * @param {array} tgn - a an array of tgn objects
 *
 * @return {string | undefined} the preferred location name
 */
export function getUlanPreferredLocation(ulanId, tgn) {
  let place = tgn.find((loc) => loc.id == ulanId);

  return place
    ? getValueByClassification(place.identified_by, aat.PREFERRED_TERM)
    : undefined;
}

/**
 * Gets all names from the ULAN Record
 *
 * @param {Object}  - an object containing the ULAN LOD and some config options:
 *  langPref - array - an array of language codes (ie. [ "en" ] )
 *  primaryName - string - the TMS primary name to filter out of ULAN results
 *  limit - number - the limit of names to return
 *
 * @returns {array} a list of names
 */
export function getUlanNames({
  ulanLod,
  langPref = [],
  primaryName = undefined,
  limit = undefined
}) {
  let result = getULANValue(ulanLod);
  // return if don't have any ULAN names
  if (!Object.keys(result.names).length) {
    return [];
  }
  // use Set for uniqueness
  let names = new Set();
  // add preferred name
  Object.values(result.preferred_name).map((val) => names.add(val));
  // return only the preferred languages
  if (langPref.length) {
    langPref.forEach((pref) => {
      result.names[pref]?.map((name) => {
        names.add(name);
      });
    });
  } else {
    Object.keys(result.names).forEach((lang) => {
      result.names[lang].forEach((v) => names.add(v));
    });
  }
  names = Array.from(names);
  // remove primaryName if included
  if (names.includes(primaryName)) {
    names.splice(names.indexOf(primaryName), 1);
  }
  // is there a display limit?
  if (limit && names.length > limit) {
    let limitedNames = [];
    for (let i = 0; i < limit; i++) {
      limitedNames.push(names[i]);
    }
    return limitedNames;
  }
  return names;
}
